import { InitProperties } from '../models/init-properties';
import { Injectable } from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';
import { format, utcToZonedTime } from 'date-fns-tz';
import { ClientsiteBaseHandler } from './clientsite-base.handler';
import { isPWA, validateTrue } from '@rx-applications/shared/utils';
import { LogChannel, LoggingMessageInterface } from '@rx-applications/shared/logging';
import { LoggingPropertyConfiguration } from '../models/logging-property-configuration';
import { NoopAmplitudeClient } from './noop.handlers';

@Injectable({ providedIn: 'root' })
export class AmplitudeHandler extends ClientsiteBaseHandler {
  protected channelToSubscribe: LogChannel = LogChannel.Amplitude;

  get instance(): any {
    return this.enabled ? amplitude : new NoopAmplitudeClient();
  }

  set Username(user: string) {
    this.instance.setUserId(user);
  }

  set IsImpersonated(impersonated: boolean) {
    const identify = new amplitude.Identify().append('isImpersonated', `${impersonated}`);
    this.instance.identify(identify);
  }

  /**
   * Appends company name, category and group to amplitude user properties
   *
   * @param name company name
   */
  set Company(name: string) {
    const category = location.hostname.includes('test852') ? 'Test' : 'Business';

    const identify = new amplitude.Identify().set('Website', name);
    identify.set('Category', category);
    this.instance.identify(identify);
    this.instance.setGroup('Company', name);
  }

  /**
   * Appends brand name to amplitude user properties
   *
   * @param brand brand name
   */
  set Brand(brand: string) {
    const identify = new amplitude.Identify().set('Brand', brand);
    this.instance.identify(identify);
  }

  /**
   * Set abtest enabled features to amplitude user properties
   * If none are enabled abtest is unset
   *
   * @param enabledFeatures array of enabled features
   */
  set AbTest(enabledFeatures: string[]) {
    const identify = new amplitude.Identify();
    const abTestKey = 'abtest';
    if (!enabledFeatures.length) {
      identify.unset(abTestKey);
    } else {
      identify.set(abTestKey, enabledFeatures);
    }
    this.instance.identify(identify);
  }

  set Role(role: string | string[]) {
    const identify = new amplitude.Identify();
    identify.set('Role', role);
    this.instance.identify(identify);
    this.instance.setGroup('Role', role);
  }

  init(initProperties: InitProperties) {
    const tracker = initProperties?.environmentTracking?.['amplitude'];
    if (!tracker) {
      return;
    }

    if (this.subscription) {
      return; // already initialized
    }

    this.enabled = validateTrue(tracker.enabled);
    this.instance.init(tracker.key, { appVersion: initProperties.appVersion });
    this.#isPWA(isPWA() as boolean);
    this.createSubscription();
  }

  createSetup(properties: LoggingPropertyConfiguration): void {
    if (properties.abTest) {
      this.AbTest = Object.keys(properties.abTest).filter((feature: string) =>
        properties.abTest ? properties.abTest[feature] : false,
      );
    }
    if (properties.company) {
      this.Company = properties.company;
    }
    if (properties.brand) {
      this.Brand = properties.brand;
    }
    if (properties.username) {
      this.Username = properties.username.email;
    }
    if (properties.isImpersonated) {
      this.IsImpersonated = properties.isImpersonated;
    }
    if (properties.role) {
      this.Role = properties.role;
    }
    if (properties.resetSession) {
      this.resetSessionId();
    }
  }

  protected override messageHandler = (value: LoggingMessageInterface) => {
    const nowDate = new Date();
    let bstDate = nowDate;
    try {
      bstDate = utcToZonedTime(nowDate, 'Europe/Dublin');
    } catch (error) {
      /* no action taken */
    }
    this.instance.logEvent(value.name, {
      ...value.props,
      hourLocal: format(nowDate, 'H'), // 0, 1, ... 23
      dayLocal: format(nowDate, 'EEE'), // Sun, Mon, ..., Sat
      hourBST: format(bstDate, 'H'),
      dayBST: format(bstDate, 'EEE'),
    });
  };

  #isPWA(isProgressiveWebApp: boolean) {
    const identify = new amplitude.Identify();
    identify.set('isPWA', isProgressiveWebApp);
    this.instance.identify(identify);
  }

  resetSessionId() {
    this.instance.setSessionId(new Date().getTime());
  }
}
